import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card } from "react-bootstrap";
import { push } from "store/actions/router";
import { each, size } from "lodash";
import { getPendingApprovals } from "actions/adminActions";
import { createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import type { RootState } from "types/state";

import Button from "components/Button";
import IconSpinner from "components/IconSpinner";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";

import "./Approval.scss";

/**
 * Local shape for a pending approval row. The `admin.approvalsById` slice is
 * `Record<string, unknown>` (state.ts), and this file dispatches an action
 * creator rather than holding an inline gql document — so the row shape used by
 * the table columns / mapStateToProps is modeled here, not pulled from codegen.
 */
interface PendingApproval {
  id: string;
  status: string;
  correlationName: string | null;
  entityIdentifier: { id: string; type: string };
  type: { label: string; urlPath: string };
}

const isLoadingSelector = createLoadingSelector(
  adminConstants.GET_PENDING_APPROVALS
);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isLoadingSelector(state) || !state.admin.approvalsById,
    // admin.approvalsById is Record<string, unknown> — narrow each value to the
    // local row shape so .status (and the table columns) are typed.
    pendingApprovals: (
      Object.values(state.admin.approvalsById) as PendingApproval[]
    ).filter((approval) => approval.status === "PENDING"),
  };
};

const mapDispatchToProps = {
  push,
  getPendingApprovals,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

class ApprovalTable extends React.PureComponent<Props> {
  constructor(props: Props) {
    super(props);
  }

  // found-not-fixed: elements are the dispatchFullLifecycle promise (a plain
  // Promise with no `.cancel`), but componentWillUnmount calls `.cancel()` on
  // each — so unmount throws at runtime. Typed loosely to preserve the call.
  _promises: unknown[] = [];

  componentDidMount() {
    this._promises.push(
      this.props.getPendingApprovals(this.props.client as ApolloClient<object>)
    );
  }

  componentWillUnmount() {
    // found-not-fixed: `.cancel()` does not exist on the returned promise (see
    // _promises) — cast preserves the original unconditional call.
    each(this._promises, (promise) =>
      (promise as { cancel: () => void }).cancel()
    );
  }

  _pendingApprovalColumns = (): Array<IconTableColumn<PendingApproval>> => {
    const columns: Array<IconTableColumn<PendingApproval>> = [
      {
        label: "Approval Type",
        key: "type.label",
      },
      {
        label: "Type",
        key: "entityIdentifier",
        format: (entity) => {
          if (
            (entity as PendingApproval["entityIdentifier"]).type === "Employer"
          ) {
            return "Company";
          }
          return (entity as PendingApproval["entityIdentifier"]).type;
        },
      },
      {
        label: "Id",
        key: "entityIdentifier.id",
      },
      {
        label: "Name",
        key: "correlationName",
      },
      {
        label: "Action",
        customComponent: (props) => {
          return (
            <Button
              name="action"
              color="action"
              size="sm"
              type="button"
              btnLabel="View"
              onClick={() =>
                this.props.push(`/approval/${props.type.urlPath}/${props.id}`)
              }
            />
          );
        },
      },
    ];
    return columns;
  };

  render() {
    const columns = this._pendingApprovalColumns();
    const data = this.props.pendingApprovals;

    if (this.props.isFetching) {
      return <IconSpinner />;
    }
    return (
      <>
        <p className="page-title">Pending Approvals</p>
        <Card>
          <IconTableHeader
            tableHeader="Approvals"
            tableCountLabel="Pending"
            tableCount={size(this.props.pendingApprovals)}
          />
          <IconTable columns={columns} data={data} />
        </Card>
      </>
    );
  }
}

export default connector(withApollo<Props>(ApprovalTable));
